<template>
	<!-- Closed or archived: the greyer background says the ticket is out of circulation !-->
	<header class="border-b border-gray-200 px-4 py-5 sm:px-6 lg:px-8" :class="isOpen ? 'bg-white' : 'bg-gray-50'">
		<div class="flex flex-wrap items-start gap-4">
			<SupportTicketAvatar :label="requesterLabel" variant="requester"/>
			<div class="min-w-0 flex-1">
				<div class="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
					<!-- Assunto !-->
					<h2 id="message-heading" class="min-w-0 text-xl font-semibold leading-tight text-gray-900">
						{{ title }}
					</h2>
					<!-- Estado do chamado: urgência e situação, o par que decide o que fazer em seguida !-->
					<div class="flex shrink-0 items-center gap-3 text-xs">
						<div class="flex items-center">
							<span class="sr-only">Urgência</span>
							<SupportUrgencyMeter :value="urgency" readonly/>
						</div>
						<SupportTicketBadge :support-ticket="ticket"/>
					</div>
				</div>
				<!-- Meta !-->
				<dl class="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
					<div>
						<dt class="sr-only">Ticket</dt>
						<dd class="font-semibold uppercase tracking-wide text-gray-600">#{{ ticket.id }}</dd>
					</div>
					<span aria-hidden="true" class="text-gray-300">•</span>
					<div>
						<dt class="sr-only">Aberto por</dt>
						<dd>Aberto por <span class="font-medium text-gray-700">{{ requesterLabel }}</span></dd>
					</div>
					<span aria-hidden="true" class="text-gray-300">•</span>
					<div>
						<dt class="sr-only">Mensagens</dt>
						<dd>{{ messageCount }} {{ messageCount === 1 ? 'mensagem' : 'mensagens' }}</dd>
					</div>
					<template v-if="lastActivity">
						<span aria-hidden="true" class="text-gray-300">•</span>
						<div>
							<dt class="sr-only">Última movimentação</dt>
							<dd>Última movimentação em {{ lastActivity }}</dd>
						</div>
					</template>
				</dl>
				<!-- Participantes !-->
				<template v-if="addressList.length">
					<div class="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1.5">
						<span class="text-[11px] font-semibold uppercase tracking-wide text-gray-400">Participantes</span>
						<SupportTicketParticipants :participants="addressList"/>
					</div>
				</template>
			</div>
		</div>
		<slot/>
	</header>
</template>

<script>
import SupportTicketStatus from "@/enums/support_ticket_status.js"

import SupportTicketBadge from "@/domain/supports/SupportTicketBadge.vue"
import SupportUrgencyMeter from "@/domain/supports/SupportUrgencyMeter.vue"
import SupportTicketParticipants from "./SupportTicketParticipants.vue"
import SupportTicketAvatar from "./SupportTicketAvatar.vue"

export default {
	name: "SupportTicketConversationHeader",
	components: {SupportTicketAvatar, SupportTicketBadge, SupportUrgencyMeter, SupportTicketParticipants},
	props: {
		ticket: {
			type: Object,
			required: true
		}
	},
	computed: {
		/**
		 * Open or waiting ticket: still in circulation
		 * @returns {boolean}
		 */
		isOpen() {
			return this.ticket.support_ticket_status == SupportTicketStatus.OPEN
				|| this.ticket.support_ticket_status == SupportTicketStatus.WAITING
		},
		title() {
			return this.$localize(this.$data_get(this.ticket, "subject.title_locale"))
		},
		/**
		 * Urgência do chamado. Chamados anteriores ao campo caem na urgência do assunto.
		 * @returns {Number|String|undefined}
		 */
		urgency() {
			return this.ticket?.urgency ?? this.$data_get(this.ticket, "subject.urgency")
		},
		requesterLabel() {
			return this.$data_get(this.ticket, "created_by.label", "—")
		},
		addressList() {
			return this.ticket?.addresses ?? []
		},
		messageCount() {
			return this.ticket?.messages?.length ?? 0
		},
		lastActivity() {
			const messages = this.ticket?.messages ?? []
			return this.$displayDateTime(messages[messages.length - 1]?.created_at)
		}
	}
}
</script>
